/**
 * Computes CSS positioning styles for the live page preview (PageCard components).
 *
 * Coordinate system: paper-space percentages, origin top-left, Y increases downward.
 *   - paperWidth / paperHeight are the logical paper dimensions (any unit — only ratios matter).
 *   - All draw positions are computed in the same unit, then converted to % of the full sheet.
 *   - Y is inverted at output: CSS `top` = ((paperHeight - centerY) / paperHeight) * 100%
 *     because the layout math below treats Y=0 as the bottom of the sheet (matching PDF
 *     convention), and CSS `top` counts from the top.
 *
 * The layout math mirrors `applyTransformations` in pdfProcessing.js exactly — same formulas,
 * same two modes (scaleFromSheetCenter vs. per-side). The only differences are:
 *   - Units: here everything is in percentage of paperWidth/paperHeight; in pdfProcessing.js
 *     units are absolute PDF points.
 *   - Aspect-ratio fitting: pdfProcessing.js fits the source page into the slot while preserving
 *     its aspect ratio. Here the PageCard element is assumed to already fill its slot, so no
 *     explicit fit step is needed.
 *
 * See pdfProcessing.js → applyTransformations() for the equivalent PDF-point implementation.
 */
export const computePreviewTransforms = ({
  paperWidth,
  paperHeight,
  pdfScale = 1,
  pdfTranslateX = 0,
  pdfTranslateY = 0,
  scaleFromSheetCenter = false,
}) => {
  if (!paperWidth || !paperHeight) {
    return null;
  }

  const sanitizeNumber = (value, fallback = 0) => {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : fallback;
  };

  const normalizedWidth = sanitizeNumber(paperWidth, 1);
  const normalizedHeight = sanitizeNumber(paperHeight, 1);
  const normalizedScale = sanitizeNumber(pdfScale, 1);
  const normalizedTranslateX = sanitizeNumber(pdfTranslateX, 0);
  const normalizedTranslateY = sanitizeNumber(pdfTranslateY, 0);
  const useSheetCenter = Boolean(scaleFromSheetCenter);

  const buildStyleForSide = (side) => {
    // Each side occupies half the sheet width. baseX is the left edge of this slot.
    const halfWidth = normalizedWidth / 2;
    const baseX = side === 'left' ? 0 : halfWidth;
    const baseY = 0;
    const baseWidth = halfWidth;
    const baseHeight = normalizedHeight;

    let drawX;
    let drawY;
    let drawWidth;
    let drawHeight;

    if (useSheetCenter) {
      // Scale relative to the sheet's horizontal center so both pages stay
      // symmetric about the fold line regardless of scale.
      const sheetCenterX = normalizedWidth / 2;
      const scaledWidth = baseWidth * normalizedScale;
      drawWidth = scaledWidth;
      drawHeight = baseHeight * normalizedScale;
      // Center the scaled page vertically within the slot, then apply Y translation.
      drawY = baseY + normalizedTranslateY + (baseHeight - drawHeight) / 2;
      // Left page grows leftward from center; right page grows rightward.
      // X translation pushes both pages away from / toward the fold.
      drawX = side === 'left'
        ? sheetCenterX - scaledWidth - normalizedTranslateX
        : sheetCenterX + normalizedTranslateX;
    } else {
      // Scale relative to each page's own center.
      // X translation direction is mirrored per side so both pages move in the same
      // visual direction (positive X = toward the outer edge on each side).
      const xOffset = side === 'left' ? -normalizedTranslateX : normalizedTranslateX;
      drawWidth = baseWidth * normalizedScale;
      drawHeight = baseHeight * normalizedScale;
      drawX = baseX + xOffset + (baseWidth - drawWidth) / 2;
      drawY = baseY + normalizedTranslateY + (baseHeight - drawHeight) / 2;
    }

    // Convert the draw rectangle to CSS: anchor at center, express as % of full sheet.
    // top is inverted because CSS Y=0 is the top, but our math has Y=0 at the bottom.
    const centerX = drawX + drawWidth / 2;
    const centerY = drawY + drawHeight / 2;

    const leftPercent = (centerX / normalizedWidth) * 100;
    const topPercent = ((normalizedHeight - centerY) / normalizedHeight) * 100;
    const widthPercent = (drawWidth / normalizedWidth) * 100;
    const heightPercent = (drawHeight / normalizedHeight) * 100;

    return {
      position: 'absolute',
      width: `${widthPercent}%`,
      height: `${heightPercent}%`,
      left: `${leftPercent}%`,
      top: `${topPercent}%`,
      transform: `translate(-50%, -50%)`,
      transformOrigin: 'center center',
    };
  };

  return {
    left: buildStyleForSide('left'),
    right: buildStyleForSide('right'),
  };
};

export default computePreviewTransforms;
